import './_download-file-styles.scss';

/**
 * Download File Block - Frontend Handler
 *
 * Handles download and view actions for buttons within download-file blocks.
 * Uses event delegation for optimal performance and memory management.
 *
 * @package
 * @since 1.0.0
 */

/**
 * DownloadFileHandler Class
 *
 * Implements singleton pattern for efficient event handling.
 * Follows SOLID principles and ensures no memory leaks.
 */
class DownloadFileHandler {

	/**
	 * Singleton instance
	 *
	 * @type {DownloadFileHandler|null}
	 */
	static instance = null;

	/**
	 * Bound event handler reference for cleanup
	 *
	 * @type {Function|null}
	 */
	boundHandler = null;

	/**
	 * Bound beforeunload handler reference for cleanup
	 *
	 * @type {Function|null}
	 */
	boundBeforeUnloadHandler = null;

	/**
	 * Get singleton instance
	 *
	 * @return {DownloadFileHandler} Handler instance
	 */
	static getInstance() {

		if ( !DownloadFileHandler.instance ) {

			DownloadFileHandler.instance = new DownloadFileHandler();

		}

		return DownloadFileHandler.instance;

	}

	/**
	 * Constructor - Initialize event listeners
	 */
	constructor() {

		if ( DownloadFileHandler.instance ) {

			return DownloadFileHandler.instance;

		}

		this.boundHandler = this.handleButtonClick.bind( this );
		this.boundBeforeUnloadHandler = this.destroy.bind( this );
		this.init();

	}

	/**
	 * Initialize event listeners using delegation
	 *
	 * @return {void}
	 */
	init() {

		if ( typeof document === 'undefined' ) {

			return;

		}

		// Use capture phase for better performance
		document.addEventListener( 'click', this.boundHandler, true );

		// Cleanup on page unload - use bound handler for proper cleanup
		window.addEventListener( 'beforeunload', this.boundBeforeUnloadHandler, {
			once: true,
		} );

	}

	/**
	 * Handle button clicks within download-file blocks
	 *
	 * @param {Event} event Click event
	 * @return {void}
	 */
	handleButtonClick( event ) {

		const button = event.target.closest(
			'.b-download-file__buttons .wp-block-button__link',
		);

		if ( !button ) {

			return;

		}

		const downloadFileBlock = button.closest( '.b-download-file' );

		if ( !downloadFileBlock ) {

			return;

		}

		const fileUrl = downloadFileBlock.dataset.fileUrl;
		const fileName = downloadFileBlock.dataset.fileName;

		if ( !fileUrl ) {

			return;

		}

		// Determine action based on button position
		const buttonsWrapper = button.closest( '.wp-block-buttons' );
		const allButtons =
			buttonsWrapper?.querySelectorAll( '.wp-block-button__link' ) || [];
		const buttonIndex = Array.from( allButtons ).indexOf( button );

		// First button = Download, Second button = View
		const action = buttonIndex === 0 ? 'download' : 'view';

		// Only handle Download button in JavaScript
		// View button uses native HTML behavior (target="_blank" in PHP)
		if ( action === 'download' ) {

			event.preventDefault();
			event.stopPropagation();
			this.downloadFile( fileUrl, fileName );

		}

		// View button: Let the browser handle it naturally with target="_blank"

	}

	/**
	 * Download file to user's device
	 *
	 * @param {string} url      File URL
	 * @param {string} fileName File name for download
	 * @return {void}
	 */
	downloadFile( url, fileName ) {

		try {

			const link = document.createElement( 'a' );
			link.href = url;
			link.download = fileName || 'download';
			link.rel = 'noopener noreferrer';

			// Accessibility: Announce download to screen readers
			link.setAttribute( 'aria-label', `Download ${fileName || 'file'}` );

			// Trigger download
			document.body.appendChild( link );
			link.click();

			// Cleanup
			setTimeout( () => {

				document.body.removeChild( link );

			}, 100 );

		} catch ( error ) {

			// Fallback: Open in new window
			window.open( url, '_blank', 'noopener,noreferrer' );

		}

	}

	/**
	 * Cleanup event listeners
	 *
	 * Prevents memory leaks by removing all event listeners
	 *
	 * @return {void}
	 */
	destroy() {

		if ( this.boundHandler ) {

			document.removeEventListener( 'click', this.boundHandler, true );
			this.boundHandler = null;

		}

		if ( this.boundBeforeUnloadHandler ) {

			// Note: beforeunload listener with once:true removes itself automatically
			// This is just for completeness
			this.boundBeforeUnloadHandler = null;

		}

		DownloadFileHandler.instance = null;

	}

}

// Initialize handler when DOM is ready
if ( document.readyState === 'loading' ) {

	document.addEventListener( 'DOMContentLoaded', () => {

		DownloadFileHandler.getInstance();

	} );

} else {

	DownloadFileHandler.getInstance();

}
